// Typed API client for /v1/knowledge/*.
//
// Wire shape mirrors backend/app/schemas/knowledge.py — payload sections
// are keyed by variable NAMES (not column indices), so the same KO is
// reusable across datasets that share column names.

import { api } from '@/lib/api';

export type KnowledgeKind = 'edges_required' | 'edges_forbidden' | 'tiers' | 'temporal';

export interface KnowledgePayload {
  edges_forbidden: [string, string][];
  edges_required: [string, string][];
  // Each tier is an ordered group of variable names. Earlier tiers may
  // cause later tiers, never the reverse.
  tiers: string[][];
}

export interface KnowledgeObject {
  id: string;
  org_id: string;
  dataset_id: string | null;
  name: string;
  kinds: KnowledgeKind[];
  payload: KnowledgePayload;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeListResponse {
  items: KnowledgeObject[];
  next_cursor: string | null;
}

export interface CreateKnowledgeBody {
  name: string;
  dataset_id?: string | null;
  // Server recomputes kinds from payload — sending a hint is fine but
  // unused. We send the derived set anyway so the request mirrors what
  // the row will ultimately hold.
  kinds: KnowledgeKind[];
  payload: KnowledgePayload;
}

export type PatchKnowledgeBody = Partial<Pick<CreateKnowledgeBody, 'name' | 'payload'>>;

export async function createKnowledge(body: CreateKnowledgeBody): Promise<KnowledgeObject> {
  const resp = await api.post<KnowledgeObject>('/knowledge', body);
  return resp.data;
}

export interface ListKnowledgeParams {
  dataset_id?: string;
  cursor?: string;
  limit?: number;
}

export async function listKnowledge(
  params: ListKnowledgeParams = {},
): Promise<KnowledgeListResponse> {
  const resp = await api.get<KnowledgeListResponse>('/knowledge', { params });
  return resp.data;
}

export async function getKnowledge(id: string): Promise<KnowledgeObject> {
  const resp = await api.get<KnowledgeObject>(`/knowledge/${id}`);
  return resp.data;
}

export async function patchKnowledge(
  id: string,
  body: PatchKnowledgeBody,
): Promise<KnowledgeObject> {
  const resp = await api.patch<KnowledgeObject>(`/knowledge/${id}`, body);
  return resp.data;
}

export async function deleteKnowledge(id: string): Promise<void> {
  await api.delete(`/knowledge/${id}`);
}

// Helper: derive `kinds` array from a payload (server does the same).
// Keeps the create/patch payload self-consistent in the form layer.
export function deriveKinds(payload: KnowledgePayload): KnowledgeKind[] {
  const out: KnowledgeKind[] = [];
  if (payload.edges_forbidden.length > 0) out.push('edges_forbidden');
  if (payload.edges_required.length > 0) out.push('edges_required');
  if (payload.tiers.length > 0) out.push('tiers');
  return out;
}
